import {
  Button,
  Checkbox,
  DataTable,
  Pagination,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { Layers, Pencil, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type {
  PolicyBatchItem,
  PolicyBatchKey,
  PolicyBatchPatch,
  PolicyBatchReleaseBlock,
  PolicyBatchReleaseBlockReason,
} from '@/services/integrations';
import {
  getPolicyBatchStatusColor,
  getPolicyBatchStatusLabel,
  POLICY_BATCH_STATUS_FILTER_OPTIONS,
  PolicyBatchStatus,
} from '@/shared/enums/policy-batch-status.enum';
import {
  ConfirmActionModal,
  SearchFilterField,
  SelectFilterField,
} from '@/shared/ui';
import { toaster } from '@/shared/ui/toaster.component';
import { f, useFilters, formatDateToBrazil } from '@/shared/utils';
import { FilterCombobox } from '@/features/reports/components/shared/filter-combobox.component';
import { usePolicyBatch } from '../hooks/use-policy-batch.hook';
import {
  useBulkUpdatePolicyBatch,
  useCheckPolicyBatchRelease,
  useDeletePolicyBatch,
  usePolicyBatchFacets,
  usePolicyBatchSummary,
  useUpdatePolicyBatch,
} from '../hooks/use-policy-batch-mutations.hook';
import { useUploadPolicyBatch } from '../hooks/use-upload-policy-batch.hook';
import {
  buildChannelCodeOptions,
  buildLobOptions,
  buildPolicyBatchKey,
  formatLobOption,
  withAllOption,
  withCurrentOption,
} from '../utils/policy-batch-options.util';
import { PolicyBatchBulkEditModal } from './policy-batch-bulk-edit-modal.component';
import { PolicyBatchEditModal } from './policy-batch-edit-modal.component';
import { PolicyBatchSummaryCards } from './policy-batch-summary-cards.component';
import { UploadBatchModal } from './upload-batch-modal.component';

/** Ação pendente de confirmação por digitação. */
type PendingAction =
  | { kind: 'edit'; item: PolicyBatchItem; patch: PolicyBatchPatch }
  | { kind: 'delete'; item: PolicyBatchItem }
  | {
      kind: 'bulk';
      items: PolicyBatchKey[];
      patch: PolicyBatchPatch;
      /** Recusadas pela pré-checagem do i4Pro — exibidas na confirmação. */
      blocked: PolicyBatchReleaseBlock[];
    }
  | null;

interface ColumnHandlers {
  selection: Map<string, PolicyBatchItem>;
  pageItems: PolicyBatchItem[];
  onToggle: (item: PolicyBatchItem) => void;
  onTogglePage: (checked: boolean) => void;
  onEdit: (item: PolicyBatchItem) => void;
  onDelete: (item: PolicyBatchItem) => void;
}

function StatusTag({ status }: { status: string }) {
  return (
    <Tag.Root
      variant='outline'
      colorScheme={getPolicyBatchStatusColor(status)}
      size='sm'
    >
      <Tag.Label>{getPolicyBatchStatusLabel(status)}</Tag.Label>
    </Tag.Root>
  );
}

function createColumns(
  handlers: ColumnHandlers
): DataTableColumn<PolicyBatchItem>[] {
  const { selection, pageItems, onToggle, onTogglePage, onEdit, onDelete } =
    handlers;

  const selectedOnPage = pageItems.filter(item =>
    selection.has(buildPolicyBatchKey(item))
  ).length;
  const allOnPageSelected =
    pageItems.length > 0 && selectedOnPage === pageItems.length;

  return [
    {
      id: 'select',
      size: 40,
      pinned: 'left',
      sortable: false,
      header: () => (
        <Checkbox.Root
          size='small'
          checked={allOnPageSelected}
          indeterminate={selectedOnPage > 0 && !allOnPageSelected}
          onCheckedChange={details => onTogglePage(details.checked === true)}
          aria-label='Selecionar todos da página'
        >
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.HiddenInput />
        </Checkbox.Root>
      ),
      cell: ({ row }) => (
        <Checkbox.Root
          size='small'
          checked={selection.has(buildPolicyBatchKey(row.original))}
          onCheckedChange={() => onToggle(row.original)}
          aria-label={`Selecionar apólice ${row.original.policyNumber}`}
        >
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.HiddenInput />
        </Checkbox.Root>
      ),
    },
    {
      accessorKey: 'policyNumber',
      header: 'Nº APÓLICE',
      size: 145,
      cell: ({ row }) => (
        <span className='font-medium text-alba-gray-700 text-sm'>
          {row.original.policyNumber}
        </span>
      ),
    },
    {
      accessorKey: 'lobId',
      header: 'RAMO',
      size: 88,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>{row.original.lobId}</span>
      ),
    },
    {
      accessorKey: 'channelCode',
      header: 'ORIGEM EMISSÃO',
      size: 108,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.channelCode ?? '-'}
        </span>
      ),
    },
    {
      accessorKey: 'batchId',
      header: 'Nº BALDE',
      size: 92,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.batchId}
        </span>
      ),
    },
    {
      accessorKey: 'requestId',
      header: 'ID SOLICITAÇÃO',
      size: 132,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.requestId}
        </span>
      ),
    },
    {
      accessorKey: 'batchDate',
      header: 'DATA BALDE',
      size: 100,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {formatDateToBrazil(row.original.batchDate)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'STATUS',
      size: 104,
      cell: ({ row }) => <StatusTag status={row.original.status} />,
    },
    {
      accessorKey: 'reservedDate',
      header: 'DATA RESERVA',
      size: 110,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.reservedDate
            ? formatDateToBrazil(row.original.reservedDate)
            : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'usedDate',
      header: 'DATA USO',
      size: 110,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.usedDate
            ? formatDateToBrazil(row.original.usedDate)
            : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'userName',
      header: 'USUÁRIO',
      size: 106,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.userName || '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'AÇÕES',
      size: 84,
      pinned: 'right',
      sortable: false,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <button
            type='button'
            title='Editar'
            aria-label={`Editar apólice ${row.original.policyNumber}`}
            onClick={() => onEdit(row.original)}
            className='rounded-lg p-2 text-alba-gray-600 transition-colors hover:bg-alba-gray-200 hover:text-alba-black-500'
          >
            <Pencil className='h-4 w-4' />
          </button>
          <button
            type='button'
            title='Excluir'
            aria-label={`Excluir apólice ${row.original.policyNumber}`}
            onClick={() => onDelete(row.original)}
            className='rounded-lg p-2 text-alba-gray-600 transition-colors hover:bg-alba-gray-200 hover:text-alba-error-red-600'
          >
            <Trash2 className='h-4 w-4' />
          </button>
        </div>
      ),
    },
  ];
}

export function PolicyBatchPage() {
  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    lobId: f.string().withDefault(''),
    channelCode: f.string().withDefault(''),
    status: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<PolicyBatchItem | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Seleção própria em vez do `enableSelection` do DataTable: o DS entrega a seleção
  // ANTERIOR no callback e chaveia por índice de linha, o que com paginação server-side
  // perde tudo ao trocar de página. A chave aqui é a mesma PK+SK do backend.
  const [selection, setSelection] = useState<Map<string, PolicyBatchItem>>(
    new Map()
  );

  const uploadMutation = useUploadPolicyBatch();
  const updateMutation = useUpdatePolicyBatch();
  const deleteMutation = useDeletePolicyBatch();
  const bulkMutation = useBulkUpdatePolicyBatch();
  const releaseCheckMutation = useCheckPolicyBatchRelease();

  const { data: facets } = usePolicyBatchFacets();
  const { data: summary, isLoading: isSummaryLoading } =
    usePolicyBatchSummary();

  const { data, isLoading, refetch, isFetching } = usePolicyBatch({
    lobId: filters.lobId ? Number(filters.lobId) : undefined,
    channelCode: filters.channelCode ? Number(filters.channelCode) : undefined,
    status: filters.status || undefined,
    search: debouncedFilters.search || undefined,
    pageNumber: filters.pageNo,
    pageSize: filters.pageSize,
  });

  const pageItems = useMemo(
    () => (data?.data ?? []) as PolicyBatchItem[],
    [data]
  );
  const selectedItems = useMemo(() => [...selection.values()], [selection]);

  const lobOptions = useMemo(
    () =>
      withAllOption(withCurrentOption(buildLobOptions(facets), filters.lobId)),
    [facets, filters.lobId]
  );
  const channelOptions = useMemo(
    () =>
      withAllOption(
        withCurrentOption(buildChannelCodeOptions(facets), filters.channelCode)
      ),
    [facets, filters.channelCode]
  );

  /** Clique num card filtra a tabela pelo pool; clicar de novo limpa. */
  const selectPool = (pool: { lobId: number; channelCode: number }) => {
    const lobId = String(pool.lobId);
    const channelCode = String(pool.channelCode);
    const isActive =
      filters.lobId === lobId && filters.channelCode === channelCode;
    setFilters({
      ...filters,
      lobId: isActive ? '' : lobId,
      channelCode: isActive ? '' : channelCode,
      pageNo: 1,
    });
  };

  const toggleItem = (item: PolicyBatchItem) =>
    setSelection(previous => {
      const next = new Map(previous);
      const key = buildPolicyBatchKey(item);
      if (next.has(key)) next.delete(key);
      else next.set(key, item);
      return next;
    });

  const togglePage = (checked: boolean) =>
    setSelection(previous => {
      const next = new Map(previous);
      pageItems.forEach(item => {
        const key = buildPolicyBatchKey(item);
        if (checked) next.set(key, item);
        else next.delete(key);
      });
      return next;
    });

  const columns = useMemo(
    () =>
      createColumns({
        selection,
        pageItems,
        onToggle: toggleItem,
        onTogglePage: togglePage,
        onEdit: setEditing,
        onDelete: item => setPending({ kind: 'delete', item }),
      }),
    [selection, pageItems]
  );

  /**
   * Salvar do lote. Quando o alvo é `Disponivel`, consulta o i4Pro para TODAS as
   * selecionadas ANTES de confirmar: liberar um número que já está em uso o devolve para a
   * fila de reserva e duplicaria a apólice. A confirmação seguinte mostra o que ficou de
   * fora e segue só com as liberáveis, em vez de o usuário descobrir os recusados no
   * relatório depois que o lote já rodou.
   */
  const handleBulkRequestSave = async (patch: PolicyBatchPatch) => {
    const keys: PolicyBatchKey[] = selectedItems.map(item => ({
      policyNumber: item.policyNumber,
      lobId: item.lobId,
    }));

    if (patch.status !== PolicyBatchStatus.Available) {
      setPending({ kind: 'bulk', items: keys, patch, blocked: [] });
      return;
    }

    const response = await releaseCheckMutation.mutateAsync(keys);

    if (!response.success || !response.data) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível consultar o i4Pro',
        description: response.error || 'Tente novamente em alguns instantes.',
      });
      return;
    }

    const { eligible, blocked } = response.data;

    if (eligible.length === 0) {
      // Sem nada a fazer: mantém o modal do lote aberto para o usuário ajustar a seleção.
      toaster.create({
        type: 'error',
        title: 'Nenhuma apólice pode ser liberada',
        description: `As ${blocked.length} selecionadas já estão em uso no i4Pro ou não puderam ser confirmadas.`,
      });
      return;
    }

    setPending({ kind: 'bulk', items: eligible, patch, blocked });
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    uploadMutation.reset();
    if (uploadMutation.data?.success) refetch();
  };

  const closePending = () => {
    setPending(null);
    setActionError(null);
  };

  const isConfirming =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    bulkMutation.isPending;

  /** Executa a ação pendente. Erro de negócio fica no modal, sem fechá-lo. */
  const confirmPending = async () => {
    if (!pending) return;
    setActionError(null);

    if (pending.kind === 'edit') {
      const response = await updateMutation.mutateAsync({
        key: {
          policyNumber: pending.item.policyNumber,
          lobId: pending.item.lobId,
        },
        patch: pending.patch,
      });
      if (!response.success)
        return setActionError(response.error || 'Erro ao salvar o registro');
      closePending();
      setEditing(null);
      return;
    }

    if (pending.kind === 'delete') {
      const response = await deleteMutation.mutateAsync({
        policyNumber: pending.item.policyNumber,
        lobId: pending.item.lobId,
      });
      if (!response.success)
        return setActionError(response.error || 'Erro ao excluir o registro');
      setSelection(previous => {
        const next = new Map(previous);
        next.delete(buildPolicyBatchKey(pending.item));
        return next;
      });
      closePending();
      return;
    }

    const response = await bulkMutation.mutateAsync({
      items: pending.items.map(item => ({
        policyNumber: item.policyNumber,
        lobId: item.lobId,
      })),
      patch: pending.patch,
    });
    if (!response.success)
      return setActionError(
        response.error || 'Erro ao aplicar a edição em lote'
      );
    // O resultado (incluindo erros por item) fica visível no modal de lote.
    closePending();
  };

  const closeBulk = () => {
    setBulkOpen(false);
    bulkMutation.reset();
    // Lote aplicado: a seleção já não corresponde ao que está na tela.
    if (bulkMutation.data?.success) setSelection(new Map());
  };

  return (
    <>
      <UploadBatchModal
        isOpen={uploadOpen}
        onClose={handleUploadClose}
        onUpload={file => uploadMutation.mutate(file)}
        isLoading={uploadMutation.isPending}
        result={uploadMutation.data}
      />

      <PolicyBatchEditModal
        item={editing}
        facets={facets}
        onClose={() => setEditing(null)}
        onRequestSave={patch =>
          editing && setPending({ kind: 'edit', item: editing, patch })
        }
      />

      <PolicyBatchBulkEditModal
        isOpen={bulkOpen}
        selected={selectedItems}
        available={pageItems}
        facets={facets}
        result={bulkMutation.data?.data ?? null}
        onClose={closeBulk}
        onSelectionChange={items =>
          setSelection(
            new Map(items.map(item => [buildPolicyBatchKey(item), item]))
          )
        }
        isChecking={releaseCheckMutation.isPending}
        onRequestSave={handleBulkRequestSave}
      />

      <ConfirmActionModal
        isOpen={Boolean(pending)}
        onClose={closePending}
        onConfirm={confirmPending}
        isLoading={isConfirming}
        errorMessage={actionError}
        tone={pending?.kind === 'delete' ? 'danger' : 'primary'}
        confirmLabel={pending?.kind === 'delete' ? 'Excluir' : 'Salvar'}
        title={confirmTitle(pending)}
        description={confirmDescription(pending)}
        warning={confirmWarning(pending)}
      />

      <div className='space-y-4 mt-4'>
        <PolicyBatchSummaryCards
          pools={summary?.pools ?? []}
          minAvailable={summary?.minAvailable ?? 0}
          loading={isSummaryLoading}
          activeLobId={filters.lobId}
          activeChannelCode={filters.channelCode}
          onSelectPool={selectPool}
        />

        <div className='flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por Nº apólice, Nº balde ou ID solicitação'
            className='w-full md:min-w-[300px] md:flex-1'
          />
          <div className='w-full md:w-[200px]'>
            <FilterCombobox
              placeholder='Ramo'
              options={lobOptions}
              value={filters.lobId}
              onChange={value => setFilters({ ...filters, lobId: value })}
            />
          </div>
          <div className='w-full md:w-[170px]'>
            <FilterCombobox
              placeholder='Origem emissão'
              options={channelOptions}
              value={filters.channelCode}
              onChange={value => setFilters({ ...filters, channelCode: value })}
            />
          </div>
          <SelectFilterField
            {...registerFilter('status')}
            items={POLICY_BATCH_STATUS_FILTER_OPTIONS}
            placeholder='Status'
            className='w-full md:w-auto md:min-w-[160px]'
            onValueChange={value => setFilters({ ...filters, status: value })}
          />
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => refetch()}
              disabled={isFetching}
              className='flex h-11 w-12 shrink-0 items-center justify-center rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 transition-colors hover:bg-alba-gray-50 hover:text-alba-gray-700 disabled:opacity-50 md:w-auto md:px-3'
              title='Atualizar'
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              />
            </button>
            <Button
              variant='primary'
              size='sm'
              onClick={() => setUploadOpen(true)}
              className='h-11 md:w-auto'
            >
              <Upload className='w-4 h-4 mr-1' />
              Importar
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setBulkOpen(true)}
              disabled={selection.size === 0}
              className='h-11 md:w-auto'
              title={
                selection.size === 0
                  ? 'Selecione ao menos uma apólice na tabela'
                  : 'Editar as apólices selecionadas'
              }
            >
              <Layers className='w-4 h-4 mr-1' />
              Edição em lote{selection.size > 0 ? ` (${selection.size})` : ''}
            </Button>
          </div>
        </div>

        {selection.size > 0 && (
          <div className='flex items-center justify-between rounded-lg border border-alba-gray-200 bg-alba-gray-50 px-4 py-2 text-sm text-alba-gray-700'>
            <span>
              {selection.size} apólice(s) selecionada(s) — a seleção é mantida
              ao trocar de página.
            </span>
            <button
              type='button'
              onClick={() => setSelection(new Map())}
              className='font-medium text-alba-gray-600 underline hover:text-alba-black-500'
            >
              Limpar seleção
            </button>
          </div>
        )}

        {/*
          Ajustes em cima do DataTable do design system:

          1. `overflow-visible` na tabela. O DS põe `overflow-hidden` no proprio <table>,
             e isso vira um scrollport entre a celula `sticky` e a div que realmente rola
             — as colunas fixas grudavam na tabela (que nao rola) em vez da viewport, ou
             seja, `pinned` nao funcionava. Os cantos arredondados continuam vindo do
             `overflow-hidden` + `rounded-3xl` deste wrapper.
          2. Fundo das colunas fixas: vem `bg-alba-white-500` (#F9F9F9), a cor do FUNDO DA
             PAGINA, enquanto as linhas sao `bg-white` (#FFFFFF). `alba-white-400` e o
             branco puro.
          3. Padding horizontal de 12px no lugar do `p-4` (16px) do DS. Com
             `table-layout: auto` a largura real de cada coluna vem do conteudo minimo
             (texto do cabecalho + icone de ordenacao + padding), nao do `size` — que e
             so uma dica. Os 8px por coluna somam ~96px em 12 colunas, o suficiente para
             a tabela caber sem scroll horizontal. O padding vertical do `p-4` fica.
        */}
        <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block [&_table]:overflow-visible [&_tbody_td:first-child]:bg-alba-white-400 [&_tbody_td:last-child]:bg-alba-white-400 [&_th]:px-3 [&_td]:px-3 [&_td:last-child]:px-2 [&_th:last-child]:px-2'>
          <DataTable
            className='min-w-0'
            loading={isLoading}
            columns={columns}
            data={pageItems}
            totalItems={data?.meta.total ?? 0}
            totalPages={data?.meta.totalPages ?? 0}
            pageSize={filters.pageSize}
            pageSizeOptions={[5, 10, 20, 30, 40, 50]}
            onPageChange={(page, pageSize) =>
              setFilters({ ...filters, pageNo: page, pageSize })
            }
          />
        </div>

        <div className='space-y-3 md:hidden'>
          {isLoading
            ? Array.from({ length: 4 }).map((_, idx) => (
                <div
                  key={idx}
                  className='h-44 animate-pulse rounded-2xl bg-alba-gray-200'
                />
              ))
            : pageItems.map(item => (
                <article
                  key={buildPolicyBatchKey(item)}
                  className='rounded-2xl border border-alba-gray-200 bg-white p-4 shadow-[0px_4px_18px_rgba(15,22,30,0.06)]'
                >
                  <div className='flex items-center justify-between'>
                    <span className='text-xs font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                      Nº Apólice
                    </span>
                    <StatusTag status={item.status} />
                  </div>
                  <p className='mt-1 text-sm font-semibold text-alba-black-500'>
                    {item.policyNumber}
                  </p>

                  <div className='my-3 h-px w-full bg-alba-gray-200' />

                  <div className='space-y-2 text-sm'>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Ramo</span>
                      <span className='text-alba-gray-800'>{item.lobId}</span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Origem Emissão</span>
                      <span className='text-alba-gray-800'>
                        {item.channelCode ?? '-'}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Nº Balde</span>
                      <span className='text-alba-gray-800'>{item.batchId}</span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>ID Solicitação</span>
                      <span className='text-alba-gray-800'>
                        {item.requestId}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Data Balde</span>
                      <span className='text-alba-gray-800'>
                        {formatDateToBrazil(item.batchDate)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Data Reserva</span>
                      <span className='text-alba-gray-800'>
                        {item.reservedDate
                          ? formatDateToBrazil(item.reservedDate)
                          : '-'}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Data Uso</span>
                      <span className='text-alba-gray-800'>
                        {item.usedDate
                          ? formatDateToBrazil(item.usedDate)
                          : '-'}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='text-alba-gray-600'>Usuário</span>
                      <span className='text-alba-gray-800'>
                        {item.userName || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='mt-3 flex justify-end gap-2 border-t border-alba-gray-200 pt-3'>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setEditing(item)}
                    >
                      <Pencil className='mr-1 h-4 w-4' />
                      Editar
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setPending({ kind: 'delete', item })}
                      className='text-alba-error-red-600'
                    >
                      <Trash2 className='mr-1 h-4 w-4' />
                      Excluir
                    </Button>
                  </div>
                </article>
              ))}

          {!isLoading && pageItems.length === 0 && (
            <div className='rounded-2xl border border-alba-gray-200 bg-white p-6 text-center text-alba-gray-600'>
              Nenhum registro encontrado.
            </div>
          )}

          {(data?.meta.totalPages ?? 0) > 0 && (
            <div className='origin-center scale-90'>
              <Pagination
                className='justify-center gap-1 [&>div]:gap-1'
                count={(data?.meta.totalPages ?? 0) * filters.pageSize}
                page={filters.pageNo}
                pageSize={filters.pageSize}
                totalItems={data?.meta.total ?? 0}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                onPageChange={p =>
                  setFilters({
                    ...filters,
                    pageNo: p.page,
                    pageSize: p.pageSize,
                  })
                }
                onPageSizeSelect={pageSize =>
                  setFilters({ ...filters, pageNo: 1, pageSize })
                }
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function confirmTitle(pending: PendingAction): string {
  if (pending?.kind === 'delete') return 'Excluir número do balde';
  if (pending?.kind === 'bulk') return 'Confirmar edição em lote';
  return 'Confirmar alteração';
}

function confirmDescription(pending: PendingAction) {
  if (!pending) return null;

  if (pending.kind === 'delete') {
    return (
      <>
        A apólice <strong>{pending.item.policyNumber}</strong> (
        {formatLobOption(pending.item.lobId)}) será removida do balde
        definitivamente.
      </>
    );
  }

  if (pending.kind === 'bulk') {
    return (
      <>
        As alterações serão aplicadas a <strong>{pending.items.length}</strong>{' '}
        apólice(s).
        {pending.blocked.length > 0 && (
          <>
            {' '}
            As outras <strong>{pending.blocked.length}</strong> não serão
            alteradas.
          </>
        )}
      </>
    );
  }

  return (
    <>
      As alterações da apólice <strong>{pending.item.policyNumber}</strong>{' '}
      serão gravadas.
    </>
  );
}

function confirmWarning(pending: PendingAction): ReactNode {
  if (!pending) return undefined;

  if (
    pending.kind === 'delete' &&
    pending.item.status === PolicyBatchStatus.Used
  ) {
    return 'Este número já foi utilizado por uma apólice emitida. Excluí-lo apaga o único vínculo entre o número e a apólice.';
  }

  // Lote já pré-checado no i4Pro: mostra exatamente o que fica de fora e o que passa.
  if (pending.kind === 'bulk' && pending.blocked.length > 0) {
    return (
      <>
        <p className='font-semibold'>
          {pending.blocked.length} apólice(s) NÃO serão liberadas:
        </p>
        <PolicyNumberList
          items={pending.blocked}
          renderSuffix={block => ` — ${RELEASE_BLOCK_LABELS[block.reason]}`}
        />

        <p className='mt-3 font-semibold'>
          Confirmando, estas {pending.items.length} passam para Disponível:
        </p>
        <PolicyNumberList items={pending.items} />
      </>
    );
  }

  if (
    pending.kind !== 'delete' &&
    pending.patch.status === PolicyBatchStatus.Available
  ) {
    return 'Liberar um número o devolve para a fila de reserva. O i4Pro é consultado antes e a operação falha se a apólice já existir lá.';
  }

  return undefined;
}

/**
 * Rótulo curto por motivo. A mensagem que vem do BFF é uma frase completa que JÁ contém o
 * nº da apólice — usá-la aqui repetiria o número, que nesta lista é o prefixo em destaque.
 */
const RELEASE_BLOCK_LABELS: Record<PolicyBatchReleaseBlockReason, string> = {
  IN_USE: 'já está em uso no i4Pro',
  INCONCLUSIVE: 'não foi possível confirmar no i4Pro',
  NOT_FOUND: 'não encontrada no balde',
};

/** Quantos números são listados antes de resumir o resto. */
const MAX_LISTED = 20;

function PolicyNumberList<T extends { policyNumber: number; lobId: number }>({
  items,
  renderSuffix,
}: {
  items: T[];
  renderSuffix?: (item: T) => string;
}) {
  return (
    <ul className='mt-1 max-h-32 space-y-1 overflow-y-auto'>
      {items.slice(0, MAX_LISTED).map(item => (
        <li key={`${item.lobId}-${item.policyNumber}`} className='text-xs'>
          <span className='font-medium'>{item.policyNumber}</span>
          {renderSuffix?.(item)}
        </li>
      ))}
      {items.length > MAX_LISTED && (
        <li className='text-xs'>... e mais {items.length - MAX_LISTED}.</li>
      )}
    </ul>
  );
}
